/* =============================================================================
 * SendDMPicker. "Send a DM" (APS-253 item 3).
 *
 * Pick a school, then one coach, then the existing DMCoachModal opens. When the
 * host passes onSent, a sent DM closes the DM window and the host. The
 * schools come from GET /integrations/:athleteId/dm-targets (list order, coaches
 * whose X handle this family can see). Each coach is then checked with X the
 * way the school card checks its DM button: a coach shows once X says they take
 * DMs from this family, waits while the check runs, and drops out on a no.
 *
 * Body only. The host supplies the overlay (BulkMessagingModal's "Send a
 * Message", or the Messages page).
 * ========================================================================== */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Heart, Lock, MessageCircle, Search } from 'react-feather';
import { XIcon } from './XIcon';
import DMCoachModal from './DMCoachModal';
import { Button, EmptyState, TextInput } from './ui';
import { integrationsAPI } from '../services/integrationsAPI';
import { DMTargets, DMTargetCoach, DMTargetSchool } from '../services/dmTargetsAPI';
import { useSocialConnect } from '../hooks/useSocialConnect';

type CheckState = 'pending' | 'yes' | 'no';
type Answer = 'yes' | 'no' | 'reauth' | 'skip';

// X answers per athlete + handle. Session cache with the school card's TTL, so
// reopening the picker does not re-ask X about every coach.
const dmCache = new Map<string, { v: boolean; t: number }>();
const DM_TTL_MS = 10 * 60 * 1000;
const CONCURRENCY = 4;
// Show the search box once the list is longer than this.
const SEARCH_AT = 6;

const keyOf = (handle: string) => handle.toLowerCase();

/** Runs at most `max` tasks at once. A priority task jumps the queue. */
function makeLimiter(max: number) {
  let active = 0;
  const queue: Array<() => void> = [];
  const next = () => {
    while (active < max && queue.length > 0) {
      active += 1;
      const run = queue.shift();
      if (run) run();
    }
  };
  return <T,>(task: () => Promise<T>, priority = false): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      const run = () => {
        task().then(resolve, reject).finally(() => {
          active -= 1;
          next();
        });
      };
      if (priority) queue.unshift(run);
      else queue.push(run);
      next();
    });
}

function cachedAnswer(athleteId: string, key: string): 'yes' | 'no' | null {
  const hit = dmCache.get(`${athleteId}:${key}`);
  if (!hit || Date.now() - hit.t > DM_TTL_MS) return null;
  return hit.v ? 'yes' : 'no';
}

/** A school row's second line. Coaches past the first yes are asked only when the school opens. */
function rowLabel(yes: number, pending: number): string {
  if (yes > 0 && pending === 0) return `${yes} coach${yes === 1 ? '' : 'es'} you can DM`;
  if (yes > 0) return 'You can DM a coach here';
  return 'Checking...';
}

const HEARTS: Record<'dream' | 'target' | 'safety', { n: number; color: string; label: string }> = {
  dream: { n: 3, color: '#10B981', label: '3-heart interest list' },
  target: { n: 2, color: '#F59E0B', label: '2-heart interest list' },
  safety: { n: 1, color: '#DC2626', label: '1-heart interest list' },
};

const HeartPill: React.FC<{ category: DMTargetSchool['category'] }> = ({ category }) => {
  if (!category) return null;
  const cfg = HEARTS[category];
  return (
    <span role="img" aria-label={cfg.label} style={{ display: 'inline-flex', gap: 2, flexShrink: 0 }}>
      {[0, 1, 2].map((i) => {
        const filled = i >= 3 - cfg.n;
        return (
          <Heart
            key={i}
            size={12}
            aria-hidden
            color={filled ? cfg.color : '#E5E7EB'}
            fill={filled ? cfg.color : 'none'}
          />
        );
      })}
    </span>
  );
};

const SchoolLogo: React.FC<{ url: string | null; size: number }> = ({ url, size }) => {
  const [failed, setFailed] = useState(false);
  if (url && !failed) {
    return (
      <img
        src={url}
        alt=""
        onError={() => setFailed(true)}
        style={{ width: size, height: size, objectFit: 'contain', borderRadius: 6, flexShrink: 0 }}
      />
    );
  }
  return (
    <span
      aria-hidden
      style={{
        width: size, height: size, borderRadius: 6, flexShrink: 0,
        background: 'var(--aps-fill)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--aps-text-subtle)',
      }}
    >
      <MessageCircle size={Math.round(size / 2)} aria-hidden />
    </span>
  );
};

interface SendDMPickerProps {
  athleteId: string;
  targets: DMTargets;
  /** Back from the school list (the host's previous step). Omit to hide it. */
  onExit?: () => void;
  /** Fires when the DM composer opens or closes over the picker. */
  onComposerOpenChange?: (open: boolean) => void;
  /** Given, a sent DM closes the DM window and this fires so the host can close too. */
  onSent?: () => void;
}

const rowStyle: React.CSSProperties = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '14px 16px',
  background: 'var(--aps-surface)',
  border: '1px solid #E5E7EB',
  borderRadius: 12,
  textAlign: 'left',
  fontFamily: 'Manrope, sans-serif',
  transition: 'border-color 0.15s',
};

const SendDMPicker: React.FC<SendDMPickerProps> = ({ athleteId, targets, onExit, onComposerOpenChange, onSent }) => {
  const [conn, setConn] = useState<'checking' | 'connected' | 'disconnected'>('checking');
  const [checks, setChecks] = useState<Record<string, CheckState>>({});
  const [needsReauth, setNeedsReauth] = useState(false);
  const [openSchoolId, setOpenSchoolId] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  const [composer, setComposer] = useState<{ coach: DMTargetCoach; school: DMTargetSchool } | null>(null);
  const runIdRef = useRef(0);
  const headingRef = useRef<HTMLHeadingElement>(null);

  const loadConnection = useCallback(async () => {
    setConn('checking');
    try {
      const status = await integrationsAPI.getConnectionStatus(athleteId);
      setConn(status.twitter ? 'connected' : 'disconnected');
    } catch {
      setConn('disconnected');
    }
  }, [athleteId]);

  useEffect(() => { loadConnection(); }, [loadConnection]);

  // Another useSocialConnect on the page may be the one exchanging the X code,
  // and this callback can fire before that exchange lands. Re-check after a
  // short wait, then once more if X still reads as disconnected.
  const recheckTimerRef = useRef<number | null>(null);
  useEffect(() => () => {
    if (recheckTimerRef.current) window.clearTimeout(recheckTimerRef.current);
  }, []);
  const recheckAfterConnect = useCallback(() => {
    setNeedsReauth(false);
    setConn('checking');
    if (recheckTimerRef.current) window.clearTimeout(recheckTimerRef.current);
    const attempt = (triesLeft: number) => {
      recheckTimerRef.current = window.setTimeout(async () => {
        try {
          const status = await integrationsAPI.getConnectionStatus(athleteId);
          if (status.twitter || triesLeft <= 0) {
            setConn(status.twitter ? 'connected' : 'disconnected');
          } else {
            attempt(triesLeft - 1);
          }
        } catch {
          setConn('disconnected');
        }
      }, 1500);
    };
    attempt(1);
  }, [athleteId]);

  const { connect, connecting } = useSocialConnect({ athleteId, onConnected: recheckAfterConnect });

  // A school makes the list once one coach there takes DMs from this family.
  // Ask X about each school's coaches in order and stop at the first yes; an
  // opened school asks about the rest (effect below). Same X call as the
  // school card's DM button.
  const requestRef = useRef<((handle: string, priority?: boolean) => Promise<Answer>) | null>(null);
  useEffect(() => {
    if (conn !== 'connected') return;
    const runId = ++runIdRef.current;
    const alive = () => runIdRef.current === runId;
    const limit = makeLimiter(CONCURRENCY);
    const inflight = new Map<string, Promise<Answer>>();

    const initial: Record<string, CheckState> = {};
    for (const school of targets.dm_targets) {
      for (const coach of school.coaches) {
        const k = keyOf(coach.twitter_handle);
        const hit = cachedAnswer(athleteId, k);
        if (hit) initial[k] = hit;
      }
    }
    setChecks(initial);
    setNeedsReauth(false);

    const request = (handle: string, priority = false): Promise<Answer> => {
      const k = keyOf(handle);
      const hit = cachedAnswer(athleteId, k);
      if (hit) return Promise.resolve(hit);
      const existing = inflight.get(k);
      if (existing) return existing;
      const p = limit(async (): Promise<Answer> => {
        if (!alive()) return 'skip';
        try {
          const result = await integrationsAPI.canSendDM(athleteId, `@${handle}`);
          if (result?.data?.reason === 'needs_reauth') return 'reauth';
          const canSend = result?.data?.can_send_dm === true;
          dmCache.set(`${athleteId}:${k}`, { v: canSend, t: Date.now() });
          return canSend ? 'yes' : 'no';
        } catch {
          return 'no';
        }
      }, priority).then((answer) => {
        inflight.delete(k);
        if (!alive()) return 'skip';
        if (answer === 'reauth') {
          // No DM permission on the family's X token. Every coach would answer
          // the same, so stop asking and show the reconnect state.
          runIdRef.current += 1;
          setNeedsReauth(true);
        } else if (answer !== 'skip') {
          setChecks((prev) => ({ ...prev, [k]: answer }));
        }
        return answer;
      });
      inflight.set(k, p);
      return p;
    };
    requestRef.current = request;

    for (const school of targets.dm_targets) {
      void (async () => {
        for (const coach of school.coaches) {
          if (!alive()) return;
          const answer = await request(coach.twitter_handle);
          if (answer !== 'no') return;
        }
      })();
    }
    return () => {
      runIdRef.current += 1;
      requestRef.current = null;
    };
  }, [conn, targets, athleteId]);

  // An opened school: ask about the coaches the list step did not need.
  useEffect(() => {
    if (openSchoolId == null || conn !== 'connected') return;
    const school = targets.dm_targets.find((s) => s.school_id === openSchoolId);
    const request = requestRef.current;
    if (!school || !request) return;
    for (const coach of school.coaches) void request(coach.twitter_handle, true);
  }, [openSchoolId, targets, conn]);

  const stateOf = useCallback((coach: DMTargetCoach): CheckState => {
    const k = keyOf(coach.twitter_handle);
    return checks[k] || cachedAnswer(athleteId, k) || 'pending';
  }, [checks, athleteId]);

  const schoolRows = useMemo(() => targets.dm_targets
    .map((school) => {
      const states = school.coaches.map(stateOf);
      return {
        school,
        yes: states.filter((s) => s === 'yes').length,
        pending: states.filter((s) => s === 'pending').length,
      };
    })
    .filter((r) => r.yes > 0 || r.pending > 0), [targets, stateOf]);

  const stillChecking = schoolRows.some((r) => r.yes === 0 && r.pending > 0);
  const q = query.trim().toLowerCase();
  const visibleRows = q ? schoolRows.filter((r) => (r.school.name || '').toLowerCase().includes(q)) : schoolRows;
  const openSchool = openSchoolId != null
    ? targets.dm_targets.find((s) => s.school_id === openSchoolId) || null
    : null;

  // Land keyboard and screen-reader focus on the new step's heading.
  useEffect(() => {
    headingRef.current?.focus();
  }, [openSchoolId]);

  const openComposer = (coach: DMTargetCoach, school: DMTargetSchool) => {
    setComposer({ coach, school });
    onComposerOpenChange?.(true);
  };
  const closeComposer = () => {
    setComposer(null);
    onComposerOpenChange?.(false);
  };

  const headingStyle: React.CSSProperties = {
    margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--aps-ink)', outline: 'none',
    fontFamily: 'Manrope, sans-serif',
  };
  const subStyle: React.CSSProperties = {
    margin: '4px 0 0', fontSize: 14, color: 'var(--aps-text-2)', fontFamily: 'Manrope, sans-serif',
  };

  const backButton = (label: string, onClick: () => void) => (
    <Button variant="ghost" size="sm" onClick={onClick} leftIcon={<ChevronLeft size={16} aria-hidden />}>
      {label}
    </Button>
  );

  const connectButton = (label: string) => (
    <Button
      variant="primary"
      size="md"
      loading={connecting === 'twitter'}
      onClick={() => connect('twitter')}
      leftIcon={<XIcon size={16} aria-hidden />}
    >
      {label}
    </Button>
  );

  let body: React.ReactNode;
  if (targets.dm_targets.length === 0) {
    body = targets.locked_school_count > 0 ? (
      <EmptyState
        icon={<Lock size={24} aria-hidden />}
        title="Unlock a school to DM its coaches"
        subtitle="Open a school from your lists and unlock it. Its coaches show up here."
      />
    ) : (
      <EmptyState
        icon={<MessageCircle size={24} aria-hidden />}
        title="No coaches to DM yet"
        subtitle="Add schools to your lists. Coaches with an X account show up here."
      />
    );
  } else if (conn === 'checking') {
    body = (
      <p role="status" style={{ ...subStyle, textAlign: 'center', padding: '32px 0' }}>
        Checking your X connection...
      </p>
    );
  } else if (conn === 'disconnected') {
    body = (
      <EmptyState
        icon={<XIcon size={24} aria-hidden />}
        title="Connect X to send DMs"
        subtitle="DMs go out from your X account."
        action={connectButton('Connect X')}
      />
    );
  } else if (needsReauth) {
    body = (
      <EmptyState
        icon={<XIcon size={24} aria-hidden />}
        title="Reconnect X to send DMs"
        subtitle="Your X connection does not allow DMs yet."
        action={connectButton('Reconnect X')}
      />
    );
  } else if (openSchool) {
    const coaches = openSchool.coaches.filter((c) => stateOf(c) !== 'no');
    body = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {coaches.map((coach) => {
          const state = stateOf(coach);
          const ready = state === 'yes';
          const role = coach.title || coach.position;
          return (
            <button
              key={coach.production_id}
              type="button"
              disabled={!ready}
              onClick={() => openComposer(coach, openSchool)}
              aria-label={ready ? `DM ${coach.coach_name}` : `${coach.coach_name}, checking`}
              style={{ ...rowStyle, cursor: ready ? 'pointer' : 'default', opacity: ready ? 1 : 0.6 }}
              onMouseEnter={(e) => { if (ready) e.currentTarget.style.borderColor = '#D1D5DB'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; }}
            >
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 15, fontWeight: 600, color: 'var(--aps-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {coach.coach_name}
                </span>
                {role && (
                  <span style={{ display: 'block', fontSize: 13, color: 'var(--aps-text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {role}
                  </span>
                )}
                <span style={{ display: 'block', fontSize: 13, color: 'var(--aps-text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  @{coach.twitter_handle}
                </span>
              </span>
              {ready ? (
                <span style={{ display: 'inline-flex', color: 'var(--aps-text-subtle)', flexShrink: 0 }}>
                  <ChevronRight size={20} aria-hidden />
                </span>
              ) : (
                <span style={{ fontSize: 13, color: 'var(--aps-text-muted)', flexShrink: 0 }}>Checking...</span>
              )}
            </button>
          );
        })}
      </div>
    );
  } else if (schoolRows.length === 0) {
    body = (
      <EmptyState
        icon={<MessageCircle size={24} aria-hidden />}
        title="No coaches take DMs from you right now"
        subtitle="The coaches at your schools have DMs closed on X. You can still email them or tag them in a post."
      />
    );
  } else {
    body = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {schoolRows.length > SEARCH_AT && (
          <TextInput
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search your schools"
            aria-label="Search schools"
            leftIcon={<Search size={16} aria-hidden />}
          />
        )}
        {visibleRows.length === 0 ? (
          <p role="status" style={{ ...subStyle, textAlign: 'center', padding: '16px 0' }}>
            No schools match "{query.trim()}".
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {visibleRows.map(({ school, yes, pending }) => {
              const ready = yes > 0;
              return (
                <button
                  key={school.school_id}
                  type="button"
                  disabled={!ready}
                  onClick={() => { setOpenSchoolId(school.school_id); setQuery(''); }}
                  style={{ ...rowStyle, cursor: ready ? 'pointer' : 'default', opacity: ready ? 1 : 0.6 }}
                  onMouseEnter={(e) => { if (ready) e.currentTarget.style.borderColor = '#D1D5DB'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; }}
                >
                  <SchoolLogo url={school.logo_url} size={32} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 15, fontWeight: 600, color: 'var(--aps-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {school.name}
                    </span>
                    <span style={{ display: 'block', fontSize: 13, color: 'var(--aps-text-2)', marginTop: 2 }}>
                      {rowLabel(yes, pending)}
                    </span>
                  </span>
                  <HeartPill category={school.category} />
                  {ready && (
                    <span style={{ display: 'inline-flex', color: 'var(--aps-text-subtle)', flexShrink: 0 }}>
                      <ChevronRight size={20} aria-hidden />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
        {stillChecking && (
          <p role="status" style={{ ...subStyle, fontSize: 13, textAlign: 'center' }}>
            Checking which coaches take DMs on X...
          </p>
        )}
      </div>
    );
  }

  const showLockedNote = !openSchool && targets.dm_targets.length > 0 && targets.locked_school_count > 0
    && conn === 'connected' && !needsReauth;
  const lockedCount = targets.locked_school_count;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontFamily: 'Manrope, sans-serif' }}>
      {(openSchool || onExit) && (
        <div>
          {openSchool
            ? backButton('All schools', () => setOpenSchoolId(null))
            : backButton('Back', () => onExit && onExit())}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        {openSchool && <SchoolLogo url={openSchool.logo_url} size={36} />}
        <div style={{ minWidth: 0 }}>
          <h3 ref={headingRef} tabIndex={-1} style={{ ...headingStyle, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {openSchool ? openSchool.name : 'Send a DM'}
          </h3>
          <p style={subStyle}>{openSchool ? 'Pick one coach.' : 'Pick a school, then one coach.'}</p>
        </div>
      </div>

      {body}

      {showLockedNote && (
        <p style={{ ...subStyle, fontSize: 13, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
          <span style={{ display: 'inline-flex', marginTop: 2, color: 'var(--aps-text-muted)' }}>
            <Lock size={13} aria-hidden />
          </span>
          <span>
            {lockedCount} more school{lockedCount === 1 ? ' on your lists is' : 's on your lists are'} locked.
            {' '}Unlock one from its page to DM its coaches.
          </span>
        </p>
      )}

      {/* Portaled to <body> so a host Modal's focus trap does not reach into
          the DM window. */}
      {composer && createPortal(
        <DMCoachModal
          isOpen
          onClose={closeComposer}
          closeOnSend={!!onSent}
          onSent={onSent}
          coach={{
            name: composer.coach.coach_name,
            twitter_handle: composer.coach.twitter_handle,
            coach_id: String(composer.coach.production_id),
            school_id: String(composer.school.school_id),
          }}
        />,
        document.body,
      )}
    </div>
  );
};

export default SendDMPicker;
